import type { Action, Computed } from 'easy-peasy';
import { action, computed } from 'easy-peasy';
import { localeMap } from './antdLocalMap';
import Cookies from 'universal-cookie';
import { ENResources } from './en_US';
import { ESResources } from './es_ES';
import { FRResources } from './fr_FR';
import { CNResources } from './zh_CN';
import ianaTimeZones from './ianaTimeZones';
import type { TranslationKey } from './TranslationTypes';
import { defaultTranslations } from './TranslationTypes';
import { currencyFormatter } from './currencyUtils';

export type Locale = 'en_US' | 'es_ES' | 'fr_FR' | 'zh_CN';
export type I18nResources = {
  en_US: Record<TranslationKey, string>;
  es_ES: Record<TranslationKey, string>;
  fr_FR: Record<TranslationKey, string>;
  zh_CN: Record<TranslationKey, string>;
};
const LOCALE_COOKIE = 'locale';
const MAX_AGE = 60 * 60 * 24 * 365 * 5;
const cookies = new Cookies();

const activeLocale: Locale = cookies.get(LOCALE_COOKIE) || 'en_US';

export interface I18nModel {
  ianaTimeZones: string[];
  currentLang: string;
  filteredTimeZones: string[];
  localeMap: Record<Locale, any>;
  activeLocale: Locale;
  defaultLocale: Locale;
  resources: I18nResources;
  setLocale: Action<I18nModel, Locale>;
  setCurrentLang: Action<I18nModel, string>;
  activeResources: Computed<I18nModel, Record<TranslationKey, string>>;
  currencyFormatters: Record<Locale, (val: number) => string>;
  activeCurrencyFormatter: Computed<I18nModel, (val: number) => string>;
  filterTimezones: Action<I18nModel, string>;
}

export const i18nModel: I18nModel = {
  ianaTimeZones,
  localeMap,
  defaultLocale: 'en_US',
  activeLocale,
  filteredTimeZones: ianaTimeZones,
  currentLang: 'en',
  resources: {
    en_US: { ...defaultTranslations, ...ENResources },
    fr_FR: { ...defaultTranslations, ...FRResources },
    es_ES: { ...defaultTranslations, ...ESResources },
    zh_CN: { ...defaultTranslations, ...CNResources },
  },
  currencyFormatters: {
    en_US: (val: number) => currencyFormatter(val, 'USD', 'en-US'),
    fr_FR: (val: number) => currencyFormatter(val, 'USD', 'fr_FR'),
    es_ES: (val: number) => currencyFormatter(val, 'USD', 'es_ES'),
    zh_CN: (val: number) => currencyFormatter(val, 'USD', 'zh_CN'),
  },
  setLocale: action((state, payload) => {
    cookies.set(LOCALE_COOKIE, payload, { path: '/', maxAge: MAX_AGE });
    state.activeLocale = payload;
  }),
  setCurrentLang: action((state, payload) => {
    state.currentLang = payload;
  }),
  activeResources: computed((state) => {
    const activeResource = state.resources[state.activeLocale || state.defaultLocale];
    return activeResource;
  }),
  activeCurrencyFormatter: computed((state) => {
    return state.currencyFormatters[state.activeLocale || state.defaultLocale];
  }),
  filterTimezones: action((state, searchText) => {
    const unfiltered: string[] = state.ianaTimeZones.slice();
    state.filteredTimeZones = unfiltered.filter((str) => str.toLowerCase().includes(searchText.toLowerCase()));
  }),
};
